import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';
import { PayrollHeader } from '../components/payroll/PayrollHeader';
import { EmployeeBlock } from '../components/payroll/EmployeeBlock';
import { PeriodBlock } from '../components/payroll/PeriodBlock';
import { PayrollTable } from '../components/payroll/PayrollTable';
import { TotalsBlock } from '../components/payroll/TotalsBlock';
import { RightPanelAdjustments } from '../components/payroll/RightPanelAdjustments';
import {
  computeBrut,
  computeEmployeeContribs,
  computeEmployerContribs,
  computeNet,
  composePayslip
} from '../payroll/compute';
import '../styles/payroll.css';

/**
 * Page de génération de fiche de paie
 * @param {Object} props
 * @param {number} props.operatorId - ID de l'opérateur
 * @param {Function} props.onNavigate - Fonction de navigation
 * @param {number} props.year - Année (optionnel)
 * @param {number} props.month - Mois (optionnel)
 */
export default function PayrollGenerate({ operatorId, onNavigate, year: urlYear, month: urlMonth, monthlyHours }) {

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Données chargées
  const [cabinet, setCabinet] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [schoolConfig, setSchoolConfig] = useState(null);
  // Heures réelles pointées du tableau mensuel

  // Ajustements utilisateur
  const [adjustments, setAdjustments] = useState({
    year: parseInt(urlYear) || currentYear,
    month: parseInt(urlMonth) || currentMonth,
    hours_overwrite: '',
    paid_leave_days: 0,
    unpaid_leave_days: 0,
    sick_days: 0,
    overtime_hours_125: 0,
    overtime_hours_150: 0,
    days_eligible_override: '',
    // Primes
    bonus_fixed: '',
    bonus_exceptional: '',
    bonus_seniority: '',
    bonus_other: '',
    // Avantages en nature
    benefit_vehicle: '',
    benefit_housing: '',
    benefit_meals: ''
  });

  // Chargement des données
  useEffect(() => {
    if (!operatorId) {
      setError('Aucun opérateur sélectionné');
      setLoading(false);
      return;
    }

    loadData();
  }, [operatorId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔧 Payroll - Chargement des données pour operatorId:', operatorId);

      // Charger en parallèle : cabinet, employee (filtré par operatorId)
      const [cabinetResponse, employeeResponse] = await Promise.all([
        // Récupérer le cabinet
        fetch(API_ENDPOINTS.CABINETS_LIST(), {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        // Récupérer l'employé lié à l'opérateur (filtré côté serveur)
        fetch(API_ENDPOINTS.EMPLOYEES_LIST(operatorId), {
          headers: getAuthHeaders(),
          credentials: 'include'
        })
      ]);

      console.log('🔧 Cabinet response status:', cabinetResponse.status);
      console.log('🔧 Employee response status:', employeeResponse.status);

      if (!cabinetResponse.ok) {
        throw new Error(`Erreur de chargement du cabinet: ${cabinetResponse.status}`);
      }

      if (!employeeResponse.ok) {
        throw new Error(`Erreur de chargement de l'employé: ${employeeResponse.status}`);
      }

      const cabinetsData = await cabinetResponse.json();
      const employeesData = await employeeResponse.json();

      console.log('🔧 Cabinets data:', cabinetsData);
      console.log('🔧 Employees data:', employeesData);

      // Extraire le cabinet (prendre le premier du tenant)
      const currentCabinet = Array.isArray(cabinetsData) 
        ? cabinetsData[0] 
        : (cabinetsData.data?.[0] || cabinetsData);

      // Extraire l'employé (devrait être unique car filtré par operatorId)
      let currentEmployee;
      if (Array.isArray(employeesData)) {
        currentEmployee = employeesData[0];
      } else if (employeesData.data && Array.isArray(employeesData.data)) {
        currentEmployee = employeesData.data[0];
      } else {
        currentEmployee = employeesData;
      }

      if (!currentEmployee) {
        throw new Error('Aucun employé trouvé pour cet opérateur. Veuillez compléter les informations salarié.');
      }

      console.log('✅ Cabinet chargé:', currentCabinet);
      console.log('✅ Employee chargé:', currentEmployee);

      setCabinet(currentCabinet);
      setEmployee(currentEmployee);

      // Si apprenti, charger config école
      if (currentEmployee.contract_type === 'APPRENTI') {
        try {
          const schoolResponse = await fetch(API_ENDPOINTS.SCHOOL_DETAIL(operatorId), {
            headers: getAuthHeaders(),
            credentials: 'include'
          });

          if (schoolResponse.ok) {
            const schoolData = await schoolResponse.json();
            setSchoolConfig(schoolData);
            console.log('✅ Config école chargée:', schoolData);
          }
        } catch (err) {
          console.warn('⚠️ Config école non disponible:', err);
        }
      }

    } catch (err) {
      console.error('❌ Erreur de chargement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculs de paie (recalculés manuellement via bouton)
  const [payslip, setPayslip] = useState(null);

  const calculatePayslip = () => {
    if (!cabinet || !employee) return;

    try {
      // 1) Utiliser les heures réelles du tableau mensuel OU override manuel
      let totalHours;
      if (adjustments.hours_overwrite && adjustments.hours_overwrite !== '') {
        totalHours = parseFloat(adjustments.hours_overwrite);
      } else {
        totalHours = monthlyHours || 151.67;
      }

      // Calculer les heures contractuelles mensuelles
      const weeklyHours = parseFloat(employee.weekly_hours || 35);
      const contractualHours = (weeklyHours * 52) / 12; // 151.67h pour 35h/semaine

      console.log('🔧 Heures totales:', totalHours, '- Heures contractuelles:', contractualHours);

      // Calculer automatiquement les heures supplémentaires
      let hours_125 = parseFloat(adjustments.overtime_hours_125) || 0;
      let hours_150 = parseFloat(adjustments.overtime_hours_150) || 0;
      let baseHours = totalHours;

      // Si on dépasse les heures contractuelles, calculer les HS automatiquement
      if (totalHours > contractualHours) {
        const excessHours = totalHours - contractualHours;
        baseHours = contractualHours;
        
        // Les 8 premières heures d'excédent sont à 125%
        hours_125 = Math.min(excessHours, 8);
        
        // Au-delà de 8h d'excédent, c'est du 150%
        if (excessHours > 8) {
          hours_150 = excessHours - 8;
        }
        
        console.log('⚠️ HEURES SUPPLÉMENTAIRES CALCULÉES:', { hours_125, hours_150, baseHours });
      }

      // 2. Calcul brut
      const brut = computeBrut({
        salaryType: employee.salary_type || 'MENSUEL',
        baseSalary: parseFloat(employee.salary_gross_base),
        hours: baseHours,
        workingTimePct: employee.working_time_pct || 100,
        overtime: {
          hours_125: hours_125,
          hours_150: hours_150
        },
        benefits: {
          vehicle: parseFloat(adjustments.benefit_vehicle) || parseFloat(employee.benefit_vehicle_amount) || 0,
          housing: parseFloat(adjustments.benefit_housing) || parseFloat(employee.benefit_housing_amount) || 0,
          meals: parseFloat(adjustments.benefit_meals) || parseFloat(employee.benefit_meals_amount) || 0,
        },
        bonuses: {
          primeFixe: parseFloat(adjustments.bonus_fixed) || 0,
          primeExceptionnelle: parseFloat(adjustments.bonus_exceptional) || 0,
          primeAnciennete: parseFloat(adjustments.bonus_seniority) || 0,
          autresPrime: parseFloat(adjustments.bonus_other) || 0
        }
      });

      console.log('🔧 Brut calculé:', brut);

      // 3. Cotisations salariales
      const employeeContribs = computeEmployeeContribs({
        brut,
        statusCadre: employee.status_cadre || 'NON_CADRE',
        cabinet,
        employee,
        period: { year: adjustments.year, month: adjustments.month }
      });

      // 4. Cotisations patronales
      const employerContribs = computeEmployerContribs({
        brut,
        atmpRate: cabinet.taux_at_mp,
        statusCadre: employee.status_cadre || 'NON_CADRE',
        cabinet,
        employee
      });

      // 5. Net à payer
      const netResult = computeNet({
        brut,
        employeeContribs,
        withholdingRate: parseFloat(employee.withholding_rate) || 0
      });

      // 6. Composer la fiche complète
      const payslipData = composePayslip({
        cabinet,
        employee,
        period: { year: adjustments.year, month: adjustments.month },
        calculations: {
          hours: baseHours,
          brut,
          employeeContribs,
          employerContribs,
          netResult,
          overtime: { hours_125, hours_150 }
        }
      });

      console.log('✅ Fiche de paie composée:', payslipData);
      console.log('💰 Brut:', brut, '€ - Net à payer:', netResult.net_a_payer, '€');

      setPayslip(payslipData);
    } catch (err) {
      console.error('❌ Erreur de calcul de paie:', err);
      setPayslip(null);
    }
  };

  // Calculer automatiquement au chargement initial et lors des changements
  useEffect(() => {
    if (cabinet && employee) {
      calculatePayslip();
    }
  }, [cabinet, employee, schoolConfig, adjustments]);

  // Handler changement ajustement
  const handleAdjustmentChange = (field, value) => {
    setAdjustments(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handler recalcul
  const handleRecalculate = () => {
    console.log('🔄 RECALCUL DÉCLENCHÉ - adjustments:', adjustments);
    calculatePayslip();
  };

  // Export PDF (window.print)
  const handleExportPDF = () => {
    window.print();
  };

  // Valider la fiche de paie
  const handleValidatePayslip = async () => {
    if (!payslip || !employee || !operatorId) {
      alert('Données insuffisantes pour valider la fiche de paie');
      return;
    }

    // Vérification des données critiques
    if (!employee.first_name || !employee.last_name) {
      alert('Informations employé incomplètes (prénom/nom manquants)');
      return;
    }

    if (payslip.totals?.brut <= 0 || payslip.totals?.net_a_payer <= 0) {
      alert('Montants de salaire invalides (brut ou net ≤ 0)');
      return;
    }

    try {
      console.log('✅ VALIDATION DE LA FICHE DE PAIE');
      
      // Préparer les données pour l'API
      const payrollData = {
        operator_id: operatorId,
        first_name: employee.first_name,
        last_name: employee.last_name,
        salary_gross: payslip.totals?.brut || 0,
        salary_net: payslip.totals?.net_a_payer || 0,
        period_month: `${adjustments.year}-${String(adjustments.month).padStart(2, '0')}`
        // Note: created_at sera géré côté serveur avec NOW() si non fourni
        // Note: inserted_by sera géré côté serveur avec caller.id
      };

      console.log('📤 Données envoyées:', payrollData);
      console.log('🔍 Détails pour debug:', {
        operatorId,
        employeeData: employee,
        payslipTotals: payslip.totals,
        adjustments: {
          year: adjustments.year,
          month: adjustments.month
        }
      });

      // Appel API POST
      const response = await fetch(API_ENDPOINTS.PAYROLL_CREATE(), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payrollData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erreur API détaillée:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
          url: response.url
        });
        throw new Error(errorData.detail || errorData.error || `Erreur serveur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Fiche de paie validée:', result);
      
      alert('Fiche de paie validée et sauvegardée avec succès !');
      
    } catch (error) {
      console.error('❌ Erreur lors de la validation:', error);
      alert(`Erreur lors de la validation: ${error.message}`);
    }
  };

  // Retour à l'activité
  const handleBack = () => {
    onNavigate('activity');
  };

  // Vérifications de conformité
  const hasOrganismIssue = cabinet?.convention_idcc === '1619' && 
    (!cabinet.organismes?.mutuelle || !cabinet.organismes?.prevoyance);

  const hasApprenticeIssue = employee?.contract_type === 'APPRENTI' && 
    (!schoolConfig || !schoolConfig.weekly_hours || !schoolConfig.school_days?.length);

  if (loading) {
    return (
      <div className="payroll-page">
        <div className="payroll-loading">
          <div className="payroll-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payroll-page">
        <div className="payroll-error">
          <h2>Erreur</h2>
          <p>{error}</p>
          <button onClick={handleBack} className="payroll-btn">
            Retour à l'activité
          </button>
        </div>
      </div>
    );
  }

  if (!cabinet || !employee || !payslip) {
    return (
      <div className="payroll-page">
        <div className="payroll-error">
          <h2>Données insuffisantes</h2>
          <p>Impossible de générer la fiche de paie. Vérifiez que le cabinet et l'employé sont correctement configurés.</p>
          <button onClick={handleBack} className="payroll-btn">
            Retour à l'activité
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payroll-page">
      {/* Header avec actions */}
      <div className="payroll-page-header no-print">
        <button onClick={handleBack} className="payroll-btn payroll-btn-secondary">
          ← Retour
        </button>
        <div className="payroll-page-title">
          <h1>Fiche de paie - {employee.first_name} {employee.last_name}</h1>
          <p className="payroll-page-subtitle">
            {payslip.period.year} - {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][payslip.period.month - 1]}
          </p>
        </div>
        <div className="payroll-page-actions">
          <button onClick={handleExportPDF} className="payroll-btn payroll-btn-primary">
            📄 Exporter PDF
          </button>
        </div>
      </div>

      {/* Alertes de conformité */}
      {hasOrganismIssue && (
        <div className="payroll-alert payroll-alert-warning no-print">
          <div className="payroll-alert-icon">⚠️</div>
          <div className="payroll-alert-content">
            <strong>CCN 1619 : Organismes manquants</strong>
            <p>Mutuelle et prévoyance sont recommandées pour cette convention. Les lignes de cotisations peuvent être incomplètes.</p>
          </div>
        </div>
      )}

      {hasApprenticeIssue && (
        <div className="payroll-alert payroll-alert-error no-print">
          <div className="payroll-alert-icon">🚨</div>
          <div className="payroll-alert-content">
            <strong>Apprenti : Configuration école manquante</strong>
            <p>Les heures d'école et les jours d'école sont obligatoires. Veuillez compléter les informations de l'employé.</p>
          </div>
        </div>
      )}

      {/* Layout principal : split 70/30 */}
      <div className="payroll-layout">
        {/* Colonne gauche : Résumé + Fiche de paie */}
        <div className="payroll-left-column">
          {/* Résumé de la fiche de paie */}
          {payslip && (
            <div className="payroll-summary-card-left">
              <h3 className="payroll-summary-title">Résumé</h3>
              <div className="payroll-summary-grid">
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Salaire brut</span>
                  <span className="payroll-summary-value">{payslip.totals?.brut?.toFixed(2) || '0.00'} €</span>
                </div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Charges salariales</span>
                  <span className="payroll-summary-value negative">-{payslip.totals?.total_employee_contribs?.toFixed(2) || '0.00'} €</span>
                </div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Charges patronales</span>
                  <span className="payroll-summary-value negative">-{payslip.totals?.total_employer_contribs?.toFixed(2) || '0.00'} €</span>
                </div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Net à payer</span>
                  <span className="payroll-summary-value highlight">{payslip.totals?.net_a_payer?.toFixed(2) || '0.00'} €</span>
                </div>
                <div className="payroll-summary-divider"></div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Heures travaillées</span>
                  <span className="payroll-summary-value">{payslip.calculations?.hours?.toFixed(2) || '0.00'} h</span>
                </div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Heures supplémentaires</span>
                  <span className="payroll-summary-value">
                    {((payslip.calculations?.overtime?.hours_125 || 0) + (payslip.calculations?.overtime?.hours_150 || 0)).toFixed(2)} h
                  </span>
                </div>
                <div className="payroll-summary-item">
                  <span className="payroll-summary-label">Congés payés</span>
                  <span className="payroll-summary-value">{adjustments.paid_leave_days || 0} j</span>
                </div>
              </div>
            </div>
          )}

          <div className="payroll-sheet">
            <PayrollHeader cabinet={cabinet} />
            <EmployeeBlock employee={{...payslip.employee, contract_type: employee.contract_type}} />
            <PeriodBlock 
              year={payslip.period.year} 
              month={payslip.period.month} 
              hours={payslip.calculations?.hours || 0}
              leaves={{
                paid: parseFloat(adjustments.paid_leave_days) || 0,
                unpaid: parseFloat(adjustments.unpaid_leave_days) || 0,
                sick: parseFloat(adjustments.sick_days) || 0
              }}
            />
            
            <div className="payroll-separator"></div>
            
            <PayrollTable 
              employeeLines={payslip.lines.employee} 
              employerLines={payslip.lines.employer} 
            />
            
            <div className="payroll-separator"></div>
            
            <TotalsBlock totals={payslip.totals} />
            
            {/* Mentions légales */}
            <div className="payroll-footer">
              <p className="payroll-legal">
                Document généré automatiquement. À conserver sans limitation de durée (art. L3243-4 du Code du travail).
              </p>
              <p className="payroll-legal-small">
                En cas d'écart avec les barèmes officiels URSSAF/Agirc-Arrco, les valeurs légales prévalent.
              </p>
            </div>
          </div>
        </div>

        {/* Colonne droite : Panneau d'ajustements */}
        <div className="payroll-right-column no-print">
          <RightPanelAdjustments
            adjustments={adjustments}
            onAdjustmentChange={handleAdjustmentChange}
            onRecalculate={handleRecalculate}
            employee={employee}
            monthlyHours={monthlyHours}
            autoRecalc={false}
          />
        </div>
      </div>

      {/* Boutons d'action - fixés en bas, toujours visibles */}
      <div className="payroll-fixed-actions no-print">
        <button 
          onClick={handleValidatePayslip}
          className="payroll-btn payroll-validate-btn"
        >
          ✅ Valider la fiche
        </button>
        <button 
          onClick={handleRecalculate}
          className="payroll-btn payroll-recalc-btn"
        >
          🔄 Recalculer la fiche
        </button>
      </div>
    </div>
  );
}
